import React, { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { MarkdownService, MarkdownContent } from '../../pages/Help/services/markdownService';
import { isPublicRoute } from '../../common/utils/routeUtils';
import { urlMatchesPattern } from '../../pages/Help/utils/urlMatcher';
import { resolveUrlToPattern } from '../../pages/Help/utils/routeExtractor';

interface HelpContextType {
  helpContent: MarkdownContent[];
  globalEntries: MarkdownContent[];
  isLoading: boolean;
  error: string | null;
  refreshHelpContent: (pageUrl?: string) => Promise<void>;
  getHelpForCurrentPage: () => MarkdownContent[];
  getHelpForPage: (pageUrl: string) => MarkdownContent[];
}

const HelpContext = createContext<HelpContextType | undefined>(undefined);

interface HelpProviderProps {
  children: ReactNode;
}

export const HelpProvider: React.FC<HelpProviderProps> = ({ children }) => {
  const [helpContent, setHelpContent] = useState<MarkdownContent[]>([]);
  const [globalEntries, setGlobalEntries] = useState<MarkdownContent[]>([]);
  // Ref so refreshHelpContent always sees the latest globalEntries without re-creating the callback
  const globalEntriesRef = useRef<MarkdownContent[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const location = useLocation();

  // Load global (module-agnostic) entries once on mount, fetching full content for each
  useEffect(() => {
    MarkdownService.getAllMarkdowns()
      .then(async all => {
        const globalSummaries = all.filter(m => m.moduleid === 'Global');
        // Fetch full content (including attachmentList) for each global entry,
        // mirroring what refreshHelpContent does for page-specific entries.
        const fullContentPromises = globalSummaries.map(m =>
          MarkdownService.getMarkdownById(m.id).catch(() => m) // fall back to summary on error
        );
        const globals = await Promise.all(fullContentPromises);
        globalEntriesRef.current = globals;
        setGlobalEntries(globals);
      })
      .catch(() => { /* non-critical */ });
  }, []);

  const refreshHelpContent = useCallback(async (pageUrl?: string) => {
    const currentPageUrl = pageUrl || location.pathname;
    
    if (isPublicRoute(currentPageUrl)) {
      setHelpContent([]);
      return;
    }

    setIsLoading(true);
    setError(null);
    
    try {
      const patternUrl = resolveUrlToPattern(currentPageUrl);
      const markdownList = await MarkdownService.getMarkdownByPageUrl(patternUrl);
      const fullContentPromises = markdownList.map(markdown =>
        MarkdownService.getMarkdownById(markdown.id)
      );
      const fullContent = await Promise.all(fullContentPromises);
      
      // Use the ref so we always have the latest globalEntries value
      const currentGlobals = globalEntriesRef.current;
      const globalIds = new Set(currentGlobals.map(g => g.id));
      const merged = [
        ...currentGlobals,
        ...fullContent.filter(c => !globalIds.has(c.id)),
      ];
      setHelpContent(merged);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load help content');
      console.error('Error loading help content:', err);
    } finally {
      setIsLoading(false);
    }
  }, [location.pathname]);

  const getHelpForCurrentPage = (): MarkdownContent[] => {
    const filteredContent = helpContent.filter(content => content.enabled);
    
    return filteredContent;
  };

  const getHelpForPage = (pageUrl: string): MarkdownContent[] => {
    return helpContent.filter(content => 
      content.enabled && urlMatchesPattern(pageUrl, content.pageurl)
    );
  };

  // Re-run when globalEntries loads so the merge is updated
  useEffect(() => {
    if (location && !isPublicRoute(location.pathname)) {
      refreshHelpContent();
    } else {
      setHelpContent([]);
    }
  }, [location.pathname, globalEntries, refreshHelpContent]);

  const value: HelpContextType = {
    helpContent,
    globalEntries,
    isLoading,
    error,
    refreshHelpContent,
    getHelpForCurrentPage,
    getHelpForPage,
  };

  // Expose context globally for DOM injection
  useEffect(() => {
    (window as any).helpContext = {
      getHelpForCurrentPage,
      getHelpForPage,
      helpContent,
      isLoading,
      error
    };
  }, [getHelpForCurrentPage, getHelpForPage, helpContent, isLoading, error]);

  return (
    <HelpContext.Provider value={value}>
      {children}
    </HelpContext.Provider>
  );
};

export const useHelp = (): HelpContextType => {
  const context = useContext(HelpContext);
  if (context === undefined) {
    throw new Error('useHelp must be used within a HelpProvider');
  }
  return context;
};
